// react
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// store
import { newToast } from 'store/slices/uiSlice';

// capacitor
import {
  InAppBrowser,
  DefaultSystemBrowserOptions,
} from '@capacitor/inappbrowser';
import { Capacitor } from '@capacitor/core';

// sentry
import * as Sentry from '@sentry/react';

// hooks
import { useAuth } from 'hooks/useAuth';
import { useTracking, TrackingEvent } from 'hooks/useTracking';

// router
import { ROUTES } from 'router';

// helpers
import { sleep } from '@karehero/helpers/timers';
import { getIsPopoverOpen, setIsPopoverOpen } from 'globalMethods';

const customURLScheme = 'karehero://';

// Pause after Kinde redirects back, so the popover is seen to finish before it closes
const IAB_CLOSE_DELAY_MS = 500;
// close() fails until the popover has finished opening, so retry until then
const IAB_CLOSE_RETRY_MS = 250;
const IAB_CLOSE_TIMEOUT_MS = 5000;
// Bounds a close() the native side never settles, so the deadline still applies
const IAB_CLOSE_CALL_TIMEOUT_MS = 2000;

const rejectAfter = async (ms: number) => {
  await sleep(ms);
  throw new Error(`InAppBrowser.close() did not settle within ${ms}ms`);
};

const closeOnce = () =>
  Promise.race([InAppBrowser.close(), rejectAfter(IAB_CLOSE_CALL_TIMEOUT_MS)]);

/**
 * closeIab closes the popover browser after Kinde redirects back to the app,
 * retrying while it is still opening.
 */
const closeIab = async () => {
  // The open flag only decides whether pausing and retrying is worthwhile, so
  // a popover it missed is still closed
  if (!getIsPopoverOpen()) {
    try {
      await closeOnce();
      console.log('[useOpenUrl] closed an IAB not known to be open');
    } catch (err) {
      // Expected: no popover is open, so there is nothing to close or report
      console.log('[useOpenUrl] no IAB open, nothing to close');
    }
    return;
  }

  await sleep(IAB_CLOSE_DELAY_MS);
  const deadline = Date.now() + IAB_CLOSE_TIMEOUT_MS;
  for (let attempt = 1; ; attempt++) {
    // Closed meanwhile, e.g. by Android handing the redirect back to the app
    if (!getIsPopoverOpen()) {
      console.log('[useOpenUrl] IAB closed before attempt', attempt);
      return;
    }
    try {
      console.log('[useOpenUrl] closing IAB, attempt', attempt);
      await closeOnce();
      setIsPopoverOpen(false);
      console.log('[useOpenUrl] IAB closed after', attempt, 'attempt(s)');
      Sentry.addBreadcrumb({
        category: 'iab',
        message: 'IAB closed',
        level: 'info',
        data: { attempt },
      });
      return;
    } catch (err) {
      console.warn('[useOpenUrl] IAB close attempt', attempt, 'failed:', err);
      if (Date.now() >= deadline) {
        console.error('[useOpenUrl] Failed to close InAppBrowser:', err);
        Sentry.captureException(err, {
          tags: { 'iab.action': 'close' },
          extra: { attempt },
        });
        // Otherwise every later callback this session would retry and report
        // again; it still makes one attempt
        setIsPopoverOpen(false);
        return;
      }
      await sleep(IAB_CLOSE_RETRY_MS);
    }
  }
};

/**
 * getIsCustomScheme checks if the given URL is using a custom scheme.
 */
const getIsCustomScheme = (url: string) => {
  return url.startsWith(customURLScheme);
};

/**
 * formatURI formats the given URI into a URL object.
 * It handles custom schemes and ensures the URL is valid.
 */
const formatURI = (uri: string) => {
  if (getIsCustomScheme(uri)) {
    uri = uri.replace(customURLScheme, `${window.location.origin}/`);
  }

  try {
    const url = new URL(uri);
    return url;
  } catch (error) {
    console.error('Invalid URL format:', uri, error);
    return new URL(window.location.origin);
  }
};

/**
 * getIsRefresh checks if the current URL should trigger a refresh.
 */
const getIsRefresh = (uri: string) => {
  const url = formatURI(uri);
  return (
    url.pathname === ROUTES.SIGN_IN ||
    url.pathname === ROUTES.SIGN_IN_NATIVE ||
    ((url.pathname === ROUTES.AUTHENTICATE ||
      url.pathname === ROUTES.AUTHENTICATE_NATIVE) &&
      !!url.searchParams.get('code') &&
      !!url.searchParams.get('state') &&
      !!url.searchParams.get('scope')) ||
    url.pathname === ROUTES.REGISTER_MEMBER
  );
};

export const useOpenUrl = () => {
  // hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { handleNativeAuthCallback } = useAuth();
  const { track } = useTracking();

  // methods
  /**
   * openUrl processes the given URI and navigates to the appropriate path.
   * It handles custom schemes and refresh scenarios.
   */
  const openUrl = useCallback(
    async (uri: string) => {
      console.log('[useOpenUrl] opening url:', uri);
      try {
        console.log('opening url:', uri);
        const url = formatURI(uri);
        const path = `${url.pathname}${url.search}${url.hash}`;

        console.log('[useOpenUrl] formatted path:', path);
        if (getIsCustomScheme(uri) && getIsRefresh(uri)) {
          const { handled } = await handleNativeAuthCallback(uri);
          await closeIab();
          window.location.href = handled ? '/' : path;
          return;
        } else {
          try {
            console.log('[useOpenUrl] closing IAB before refresh');
            await InAppBrowser.close();
          } catch (e) {
            console.warn(
              '[useOpenUrl] InAppBrowser.close() threw (ignored):',
              e,
            );
          }
          console.log('navigating to: ', path, navigate.toString());
          navigate(path);
        }
      } catch (err) {
        console.error('Invalid URL received from Universal Link:', err);
        Sentry.captureException(err, {
          tags: { 'deeplink.action': 'open' },
          // Without the query, which can hold an auth code
          extra: { url: uri.split('?')[0] },
        });
      }
    },
    [navigate, handleNativeAuthCallback],
  );

  /**
   * openExternalUrl opens a third-party URL outside the app: the system
   * browser on native (so the user clearly leaves the app), a new tab on web.
   */
  const openExternalUrl = useCallback(
    async (url: string) => {
      console.log('[useOpenUrl] opening external url:', url);
      const platform = Capacitor.getPlatform();

      // Track the hand-off (best-effort — never block opening the URL on it).
      try {
        track(TrackingEvent.ExternalUrlOpened, { url, platform });
      } catch (err) {
        console.error('[useOpenUrl] Failed to track external url open:', err);
        Sentry.captureException(err, { extra: { url, platform } });
      }

      try {
        if (Capacitor.isNativePlatform()) {
          await InAppBrowser.openInSystemBrowser({
            url,
            options: DefaultSystemBrowserOptions,
          });
          return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.error('[useOpenUrl] Failed to open external url:', url, err);
        Sentry.captureException(err, { extra: { url, platform } });
        dispatch(
          newToast({
            title: 'Something went wrong',
            description: "We couldn't open the link. Please try again.",
            variant: 'error',
          }),
        );
      }
    },
    [track, dispatch],
  );

  return { openUrl, openExternalUrl, getIsRefresh, getIsCustomScheme };
};
